import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Grid, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import { Add, Refresh, Delete } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_COLORS = { Monday: '#1a237e', Tuesday: '#2e7d32', Wednesday: '#e65100', Thursday: '#880e4f', Friday: '#00695c', Saturday: '#6a1b9a' };

export default function FoodMenu() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ day: 'Monday', breakFast: '', launch: '', snacks: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getFoodMenu, {});
    setMenu(data?.foodmenu || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.day) { alert('Day is required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addFoodMenu, form);
    setSaving(false);
    if (data?.code === '200') { setDialog(false); setForm({ day: 'Monday', breakFast: '', launch: '', snacks: '' }); load(); }
    else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this menu?')) return;
    await InvokeApi(Config.main_url + apiList.deleteFoodMenu, { id: id });
    load();
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Food Menu</Typography>
          <Typography variant="body2" color="text.secondary">Weekly meal plan</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>Add Menu</Button>
        </Box>
      </Box>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#f5f6fa' }}>
                {['#', 'Days', 'Breakfast', 'Launch', 'Snacks', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {menu.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No food menu found
                  </TableCell>
                </TableRow>
              ) : menu.map((d, i) => (
                <TableRow key={d.id || d.ID || i} hover>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>
                    <Chip label={d.days} sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700 }} />
                  </TableCell>
                    { d.break_fast && <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{d.break_fast}</TableCell>}
                    { d.launch && <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{d.launch}</TableCell>}
                    { d.snacks && <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{d.snacks}</TableCell>}
                  {/* <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{d.section || '—'}</TableCell> */}
                  <TableCell>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(d.id || d.ID)} sx={{ color: '#c62828' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Add Food Menu</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Day *" value={form.day} onChange={f('day')}>
                {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Breakfast" value={form.breakFast} onChange={f('breakFast')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Lunch" value={form.launch} onChange={f('launch')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Snacks" value={form.snacks} onChange={f('snacks')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
